import { computed } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { cache } from 'tm3-core/cache';
import type FeatureEnum from 'tm3-core/enums/feature-enum';
import type IFeature from 'tm3-core/interfaces/feature';
import FeaturesStore from 'tm3-core/stores/feature-store';

interface IProps extends React.PropsWithChildren {
  premium?: boolean;
  fallback?: React.ReactNode;
  flag?: FeatureEnum;
  override?: boolean;
  skipConfig?: boolean;
  skipHideIf?: boolean;
  value?: IFeature;
  valueOr?: IFeature[];
  valueAnd?: IFeature[];
}

export const withFeature = <T extends U, U extends object>(
  Comp: React.ComponentType<U>,
  featureProps: IProps
) => {
  const WrappedComponent: React.FC<T> = (props: T) => {
    return (
      <Feature {...featureProps}>
        <Comp {...props} />
      </Feature>
    );
  };

  WrappedComponent.displayName = `Feature-${Comp.displayName}`;

  return WrappedComponent;
};

@observer
class Feature extends React.Component<IProps> {
  public static readonly defaultProps: Partial<IProps> = {
    premium: false
  };

  render() {
    if (!this.show) {
      return !!this.props.fallback ? <>{this.props.fallback}</> : null;
    }

    return <>{this.props.children}</>;
  }

  @computed
  get show(): boolean {
    const { flag, value, valueOr, valueAnd, override } = this.props;
    const { loaded } = FeaturesStore;

    if (!loaded) {
      return false;
    }

    if (!!override) return true; // override ignores flags

    // if just an empty wrapper show the feature
    // use in nav items that passthrough only to flag prop
    if (!flag && !value && !valueOr && !valueAnd) return true;

    if (!!flag) {
      return FeaturesStore.has([flag]);
    }
    if (!!value) return this.isAllowed(value);

    if (!!valueAnd) {
      for (let v of valueAnd) {
        if (!this.isAllowed(v)) return false;
      }
      return true;
    }
    if (!!valueOr) {
      for (let v of valueOr) {
        if (this.isAllowed(v)) return true;
      }
    }
    return false;
  }

  private isAllowed(value: IFeature): boolean {
    const { hasPremium, isTrial } = FeaturesStore;
    if (!this.props.skipHideIf && value.hideIf) return false;
    if (value.modules?.some((module) => !cache.hasAllowedModule(module))) return false;
    if (!!value.premium && !hasPremium) {
      if (!value.allowTrial || !isTrial) {
        return false;
      }
    }
    return FeaturesStore.match(value, this.props.skipConfig);
  }
}

export default Feature;
